export type PeriodKind = 'M' | 'Q' | 'Y';


export function getPeriodKind(pid: string): PeriodKind | null {
  if (/^\d{4}$/.test(pid)) return 'Y';
  if (/^\d{4}-Q[1-4]$/.test(pid)) return 'Q';
  if (/^\d{4}-\d{1,2}$/.test(pid)) return 'M';
  return null;
}
export const MONTHS_PER_KIND: Record<PeriodKind, number> = { M: 1, Q: 3, Y: 12 };

const absMonth = (y: number, m: number) => y * 12 + (m - 1);

export type CoverCell = {
  periodId: string;   // verbatim from idx.periodIdByIdx — never reformatted
  kind: PeriodKind;
  m0: number;         // absolute month of first month
  len: number;        // months covered
};

export function spanOf(pid: string): { m0: number; len: number } {
  const kind = getPeriodKind(pid);
  if (!kind) throw new Error(`Invalid periodId: ${pid}`);
  if (kind === 'Y') return { m0: absMonth(Number(pid), 1), len: 12 };
  if (kind === 'Q') {
    const y = Number(pid.slice(0, 4));
    const q = Number(pid.slice(-1));
    return { m0: absMonth(y, (q - 1) * 3 + 1), len: 3 };
  }
  const [ys, ms] = pid.split('-');
  return { m0: absMonth(Number(ys), Number(ms)), len: 1 };
}

export function buildMonthCover(periodIds: string[]): CoverCell[] {
  const byYear = new Map<number, Record<PeriodKind, CoverCell[]>>();
  for (const pid of periodIds) {
    const kind = getPeriodKind(pid);
    if (!kind) continue;
    const { m0, len } = spanOf(pid);
    const y = Math.floor(m0 / 12);
    if (!byYear.has(y)) byYear.set(y, { M: [], Q: [], Y: [] });
    byYear.get(y)![kind].push({ periodId: pid, kind, m0, len });
  }

  const out: CoverCell[] = [];
  for (const [, g] of byYear) {
    const total = (cs: CoverCell[]) => cs.reduce((s, c) => s + c.len, 0);
    // finest kind that covers the whole year; else the year row; else partial
    const pick =
      total(g.M) === 12 ? g.M :
      total(g.Q) === 12 ? g.Q :
      g.Y.length ? g.Y :
      g.M.length ? g.M : g.Q;
    out.push(...pick);
  }
  return out.sort((a, b) => a.m0 - b.m0);
}